import {max} from 'lodash-es';
import {useEffect} from 'react';
import {v4 as uid} from 'uuid';

import {IBreadcrumbItem} from 'components/breadcrumbs';
import {EEntityType} from 'shared/constants/entities';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {useAppSelector} from 'store/config/hooks';
import {BreadcrumbsSections} from 'store/slices/breadcrumbs-slice';
import {selectMenuItemByEntityName, selectMenuStructureIdMap} from 'store/slices/menu-slice';
import {selectTableReportTemplateConfig} from 'store/slices/table-report-slice/table-report-slice-selectors';

interface IUseBreadcrumbsControllerArgs {
    entityName?: string;
    docId: string | null | undefined;
    templateCode: string | undefined;
}

export const useBreadcrumbsController = ({
    entityName, docId, templateCode: templateCodeFromUrl,
}: IUseBreadcrumbsControllerArgs) => {
    const templateConfig = useAppSelector(selectTableReportTemplateConfig);

    const {
        templateCode,
        reportName,
    } = templateConfig ?? {};

    const history = useAppHistory();

    const currentMenuItem = useAppSelector(s => (
        entityName ? selectMenuItemByEntityName(s, entityName) : null
    ));
    const currentEntityType = currentMenuItem?.entityType;

    const menuIdMap = useAppSelector(selectMenuStructureIdMap);
    const parentItemTitle = menuIdMap && currentMenuItem?.parentId
        ? menuIdMap[currentMenuItem?.parentId]?.itemTitle : '';

    const breadcrumbs = history.getLocationStateValue(ELocationStateKey.TABLE_REPORT_BREADCRUMBS);

    const findNextId = () => {
        const maximum: number | undefined = max(breadcrumbs?.map((bc: any) => Number(bc.id)));
        if (maximum) return `${maximum + 1}`;
        return `${breadcrumbs?.length ?? 0 + 1}`;
    };

    const handleBreadcrumbClick: IBreadcrumbItem['onClickCallback'] = ({id, path, extraData}) => {
        if (!path) return;
        if (id === '1') {
            history.push(path);
        } else {
            const newBreadcrumbs = breadcrumbs?.filter((bc: any) => Number(bc.id) <= Number(id)) ?? [];
            history.pushWithStateUpdate(path, {
                [ELocationStateKey.TABLE_REPORT_BREADCRUMBS]: newBreadcrumbs,
                [ELocationStateKey.TABLE_REPORT_PARAMS]: extraData?.tableReportDrilldownParams,
                [ELocationStateKey.TABLE_REPORT_LOCATION_DEFAULTS]: extraData?.tableReportLocationDefaults,
                [ELocationStateKey.TABLE_REPORT_PUSH_UNIQUE_ID]: extraData?.tableReportPushUniqueId,
            });
        }
    };

    const pushWithBreadcrumbs = ({name, path, extraData}: IBreadcrumbItem) => {
        if (!path) return;

        const pushUniqueId = uid();

        const breadcrumb = {
            id: findNextId(),
            name,
            path,
            extraData: {
                ...extraData,
                tableReportPushUniqueId: pushUniqueId,
            },
        };

        const {
            tableReportDrilldownParams,
            tableReportLocationDefaults,
        } = extraData ?? {};

        const isSameReport = `${history.location.pathname}${history.location.search}` === path;

        history.pushWithStateUpdate(path, {
            [ELocationStateKey.TABLE_REPORT_BREADCRUMBS]: [
                ...(breadcrumbs ?? []),
                ...(!isSameReport ? [breadcrumb] : []),
            ],
            [ELocationStateKey.TABLE_REPORT_PARAMS]: tableReportDrilldownParams,
            [ELocationStateKey.TABLE_REPORT_LOCATION_DEFAULTS]: tableReportLocationDefaults,
            [ELocationStateKey.TABLE_REPORT_PUSH_UNIQUE_ID]: pushUniqueId,
        });
    };

    const getNextStepBreadcrumbs = ({name, path}: IBreadcrumbItem) => {
        if (!path) return null;

        const pushUniqueId = uid();

        const breadcrumb = {
            id: findNextId(),
            name,
            path,
            extraData: {
                tableReportPushUniqueId: pushUniqueId,
            },
        };

        return [
            ...(breadcrumbs ?? []),
            breadcrumb,
        ];
    };

    const defaultBreadcrumbs = [
        {
            id: '1',
            name: BreadcrumbsSections.MAIN_PAGE,
            path: '/',
        },
        ...((() => {
            if ([
                EEntityType.TABLE_REPORT,
                EEntityType.JASPER_REPORT,
            ].some(v => v === currentEntityType && parentItemTitle
                && parentItemTitle === BreadcrumbsSections.REPORTS)) {
                return [{
                    id: '2',
                    name: BreadcrumbsSections.REPORTS,
                }];
            }
            return [];
        }
        )()),
        ...(entityName ? ([{
            id: '3',
            name: reportName ?? BreadcrumbsSections.REPORTS,
            path: `/${entityName}`,
        }]) : []),
    ];

    useEffect(() => {
        if (!breadcrumbs && templateConfig && templateCode && templateCode === templateCodeFromUrl) {
            if (docId) {
                history.setLocationStateValue(ELocationStateKey.TABLE_REPORT_BREADCRUMBS, [
                    ...defaultBreadcrumbs,
                    {
                        id: '4',
                        name: reportName ?? BreadcrumbsSections.REPORT,
                        path: `/table-reports/${docId}/${templateCode}`,
                    },
                ]);
            } else {
                history.setLocationStateValue(
                    ELocationStateKey.TABLE_REPORT_BREADCRUMBS,
                    [
                        defaultBreadcrumbs[0],
                        {
                            id: '2',
                            name: reportName ?? BreadcrumbsSections.REPORT,
                            path: `/table-reports/${templateCode}`,
                        },
                    ],
                );
            }
        }
    }, [templateCode]);

    return {
        defaultBreadcrumbs,
        pushWithBreadcrumbs,
        handleBreadcrumbClick,
        getNextStepBreadcrumbs,
    };
};

export type TPushWithBreadcrumbsFunction = ReturnType<typeof useBreadcrumbsController>['pushWithBreadcrumbs'];
